import Header from "./Header";
import { Footer } from "@literati/public-ui-shared-react";
import React, { useContext, useEffect, useState } from "react";
import SubscriptionsProvider, {
  SubscriptionsContext,
  SubscriptionsDispatchContext,
} from "contexts/SubscriptionsProvider";
import Sidebar from "./Sidebar";
import Messages from "../../Layout/Messages";
import styled from "styled-components";
import useViewport from "utils/hooks/useViewport";
import { getFirstAddressFromSubscriptions } from "utils/address";
import { useRouter } from "next/router";
import cx from "classnames";
import LoadingAnimation from "@/atoms/account/LoadingAnimation";
import { AccountContext } from "contexts/AccountProvider";
import Link from "next/link";
import Highlight from "@/atoms/account/Highlight";
import { domMax, LazyMotion, MotionConfig } from "framer-motion";
import ShippingAddressModal from "components/pages/account/subscription/ShippingAddressModal";
import IneligibleAddressModal from "components/pages/account/subscription/IneligibleAddressModal";

const StyledLayout = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  font-weight: 500;
  position: relative;

  &,
  footer {
    background-color: ${({ theme }) => theme.colors["pulp-white"]} !important;
  }

  .main-container {
    flex-grow: 1;
    width: 100%;
    max-width: 960px;
    display: grid;
    column-gap: 3rem;
    margin: 0 auto;
    padding-top: 6rem;

    ${({ theme }) => theme.media["md"]} {
      grid-template-columns: minmax(240px, 2fr) minmax(0, 5fr);
      padding-top: 3rem;
    }

    &.hideSidebar {
      grid-template-columns: 1fr;
      padding-top: 0;
      aside {
        display: none;
      }
    }

    .main-content {
      opacity: 1;
      transition: 0.2s opacity ease;
    }

    .pageLoading {
      position: relative;

      .main-content {
        opacity: 0;
        transition: none;
        pointer-events: none;
      }
    }
  }
`;

const StyledFooter = styled(Footer)`
  margin-top: 5rem;
`;

const AccountLayout = ({ children, className }) => {
  const [addressModalOpen, setAddressModalOpen] = useState(true);
  const [ineligibleAddressModalOpen, setIneligibleAddressModalOpen] =
    useState(false);
  const [ineligibleAddress, setIneligibleAddress] = useState(null);
  const [subsWithoutAddresses, setSubsWithoutAddresses] = useState([]);
  const [sidebarMenuExpanded, setSidebarMenuExpanded] = useState(false);
  const toggleMenu = () => setSidebarMenuExpanded((expanded) => !expanded);
  const { screen } = useViewport();
  const { pathname, asPath, events, ...router } = useRouter();
  const [activePath, setActivePath] = useState(asPath);
  const [pageLoading, setPageLoading] = useState(true);
  const { auth, noPaymentMethod } = useContext(AccountContext);

  useEffect(() => {
    if (auth && !auth.isAuthenticated) {
      router.push(`/login?next=${asPath}`);
    }
  }, [auth, asPath]);

  useEffect(() => {
    setSidebarMenuExpanded(false);
  }, [screen, asPath, activePath]);

  const { subscriptions, canceledSubscriptions, hasNonGiftSub } =
    useContext(SubscriptionsContext);

  const { getResources, getSubscriptions } = useContext(
    SubscriptionsDispatchContext
  );

  useEffect(() => {
    if (!subscriptions) {
      getResources();
      getSubscriptions();
    }
  }, [subscriptions, getSubscriptions, getResources]);

  useEffect(() => {
    const collection = subscriptions?.filter(
      (sub) =>
        !sub.hasShippingAddress &&
        (!sub.endDate || new Date(sub.endDate) > new Date())
    );
    setSubsWithoutAddresses(collection);
  }, [subscriptions]);

  useEffect(() => {
    // Allows modal to dismiss from previous address save,
    // then re-open with next sub without an address
    if (subsWithoutAddresses?.length && !ineligibleAddressModalOpen) {
      setAddressModalOpen(true);
    }
  }, [subsWithoutAddresses, ineligibleAddressModalOpen]);

  useEffect(() => {
    setPageLoading(false);
  }, [asPath]);

  useEffect(() => {
    const handleStart = (url) => {
      if (asPath.includes("/account/") && url.includes("/account/")) {
        setActivePath(url.replace("/kids", ""));
        if (url !== `/kids${pathname}/`) {
          /** this check avoids triggering when clearing query params */
          setPageLoading(true);
        }
      }
    };

    const handleStop = () => {
      setPageLoading(false);
    };

    events.on("routeChangeStart", handleStart);
    events.on("routeChangeComplete", handleStop);
    events.on("routeChangeError", handleStop);

    return () => {
      events.off("routeChangeStart", handleStart);
      events.off("routeChangeComplete", handleStop);
      events.off("routeChangeError", handleStop);
    };
  }, [events, asPath, pathname]);

  const hideSidebar = asPath.includes("cancel");

  const handleShippingModalSuccess = () => {
    setAddressModalOpen(false);
    getResources();
    getSubscriptions();
  };

  const handleShippingAddressIneligible = (ineligibleAddress) => {
    setIneligibleAddress(ineligibleAddress);
    setIneligibleAddressModalOpen(true);
    setAddressModalOpen(false);
  };

  return (
    <>
      {Boolean(subsWithoutAddresses?.length) && (
        <ShippingAddressModal
          isOpen={addressModalOpen}
          shippingAddressProps={{
            onSuccess: handleShippingModalSuccess,
            shippingAddress: getFirstAddressFromSubscriptions(subscriptions),
            submitPristine: true,
            onIneligibleAddress: handleShippingAddressIneligible,
          }}
          subId={subsWithoutAddresses?.[0]?.id}
        />
      )}
      {ineligibleAddress && (
        <IneligibleAddressModal
          isOpen={ineligibleAddressModalOpen}
          subscriptionId={subsWithoutAddresses?.[0]?.id}
          address={ineligibleAddress}
          onCancel={() => {
            setIneligibleAddressModalOpen(false);
            setAddressModalOpen(true);
          }}
          onRefund={() => {
            addToast("Refund processed.", { appearance: "success" });
          }}
        />
      )}
      <LazyMotion features={domMax}>
        <MotionConfig transition={{ duration: 0.2 }}>
          <StyledLayout id="admin-layout" className={className}>
            <Header displayMenu={hideSidebar} />
            <Messages showBar={false} />
            <div className={"relative flex-grow"}>
              <div className={"content-spacing w-full"}>
                {subscriptions ? (
                  <div className={cx("main-container", { hideSidebar })}>
                    <aside>
                      <Sidebar
                        subscriptions={subscriptions}
                        canceledSubscriptions={canceledSubscriptions}
                        expanded={sidebarMenuExpanded}
                        toggleMenu={toggleMenu}
                        activePath={activePath}
                        isAffiliate={auth?.user?.isAffiliate}
                      />
                    </aside>

                    <main className={cx({ pageLoading })}>
                      {pageLoading && (
                        <div className={"sticky top-0 m-auto h-0"}>
                          <LoadingAnimation />
                        </div>
                      )}
                      <div className={"main-content"}>
                        {noPaymentMethod && hasNonGiftSub && (
                          <Highlight error className={"mb-4"}>
                            No usable payment method on file! Add a payment
                            method, so we can continue deliveries on
                            schedule.&nbsp;
                            <Link href={"/account/account-details#billing-a"}>
                              <a className={"font-bold text-error underline"}>
                                Update your billing information.
                              </a>
                            </Link>
                          </Highlight>
                        )}
                        {children}
                      </div>
                    </main>
                  </div>
                ) : (
                  <LoadingAnimation />
                )}
              </div>
            </div>
            <StyledFooter eventSource="literati-next-kids" />
          </StyledLayout>
        </MotionConfig>
      </LazyMotion>
    </>
  );
};

/**
 *
 * @param {Object} props
 * @param {string} [props.className]
 * @param {import('react').ReactNode} [props.children]
 */
const AccountLayoutWithProviders = ({ children, className }) => (
  <SubscriptionsProvider>
    <AccountLayout className={className}>{children}</AccountLayout>
  </SubscriptionsProvider>
);

export default AccountLayoutWithProviders;

export const getAccountLayout = (page) => (
  <AccountLayoutWithProviders>{page}</AccountLayoutWithProviders>
);
